import { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage, LANGS } from '../contexts/LanguageContext';
import { getCartCount } from '../utils/cart';
import logo from '../data/xinvana_black.png';
import SeoHead from './SeoHead';
import '../styles/xv.css';

const FlagImg = ({ cc }) => (
  <img
    src={`/flags/${cc}.png`}
    srcSet={`/flags/${cc}@2x.png 2x`}
    width="24" height="18"
    alt=""
    style={{ display: 'inline-block', flexShrink: 0 }}
  />
);

/* 세로 3줄 트리거 — 가운데 줄 오렌지 */
function BarsIcon({ light }) {
  const c = light ? '#ffffff' : '#0a0a0a';
  return (
    <svg width="22" height="28" viewBox="0 0 22 28" aria-hidden="true">
      <rect x="0" y="0" width="2.5" height="28" fill={c} />
      <rect x="9.75" y="0" width="2.5" height="28" fill="#ff4d00" />
      <rect x="19.5" y="0" width="2.5" height="28" fill={c} />
    </svg>
  );
}

/**
 * 언어 선택기.
 * 데스크톱·모바일 두 군데에 렌더되므로 ref/열림상태를 각자 갖는 컴포넌트로 둔다.
 * (하나의 ref 를 공유하면 바깥클릭 감지가 엉켜 선택 직전에 닫히는 버그가 생김)
 */
function LangPicker({ lang, setLang, invert }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="언어 선택"
        className={`text-[13px] font-semibold tracking-[0.1em] px-2 py-1 border-[1.5px] transition-colors ${
          invert ? 'border-white/60 hover:border-white' : 'border-[#0a0a0a]/30 hover:border-[#0a0a0a]'
        }`}
      >
        {lang.toUpperCase()}
      </button>
      {open && (
        <ul role="listbox" className="absolute right-0 top-full mt-2 z-[120] min-w-[168px] m-0 p-0 list-none bg-white border-[1.5px] border-[#0a0a0a] text-[#0a0a0a]">
          {Object.entries(LANGS).map(([code, l]) => (
            <li key={code} role="option" aria-selected={lang === code}>
              <button
                type="button"
                onClick={() => { setLang(code); setOpen(false); }}
                className={`flex items-center gap-2.5 w-full px-3 py-2.5 text-[14px] text-left transition-colors hover:bg-[#ff4d00] ${lang === code ? 'font-semibold' : ''}`}
              >
                <FlagImg cc={l.cc} />
                <span>{l.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const ScanGlyph = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 3v3m10-3v3M3 7h3m12 0h3M3 17h3m12 0h3M7 21v-3m10 3v-3M8 11h8m-4-4v8" />
  </svg>
);

export default function Layout() {
  const { isAuthenticated, user, logout } = useAuth();
  const { lang, setLang, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [cartCount, setCartCount] = useState(0);
  const [measureModal, setMeasureModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const mainRef = useRef(null);

  // 상품 상세는 상단이 검정 패널이라 헤더 글자를 흰색으로, 스크롤 후 검정
  const isProduct = /^\/product\/\d+/.test(location.pathname);
  const isHome = location.pathname === '/';
  // 히어로가 헤더 아래까지 꽉 차는 페이지만 상단 여백 없음. 나머지는 고정 헤더만큼 밀어준다.
  const fullBleed = isHome || isProduct;
  // 상품 상세(검정 패널) · 홈(배경 이미지 히어로) 상단에서는 헤더 글자를 흰색으로
  const invertHeader = (isProduct || isHome) && !scrolled;

  useEffect(() => {
    const update = () => setCartCount(getCartCount());
    update();
    window.addEventListener('cart-updated', update);
    return () => window.removeEventListener('cart-updated', update);
  }, []);

  // scrolled: 모바일은 main 내부 스크롤, 데스크톱은 window 스크롤
  useEffect(() => {
    const onScroll = () => {
      const el = mainRef.current;
      const y = el && el.scrollTop > 0 ? el.scrollTop : window.scrollY;
      setScrolled(y > Math.max(60, window.innerHeight - 90));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    const el = mainRef.current;
    if (el) el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (el) el.removeEventListener('scroll', onScroll);
    };
  }, [location.pathname]);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);


  const handleLogout = () => { logout(); navigate('/'); };

  const isMobileDevice = () => window.innerWidth < 768 && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
  const handleMeasureClick = (e) => {
    if (!isMobileDevice()) { e.preventDefault(); setMeasureModal(true); }
  };
  const goMeasure = (e) => {
    handleMeasureClick(e);
    if (!e.defaultPrevented) navigate('/measure/start');
  };

  const L = {
    brand: t({ ko: '브랜드', en: 'Brand', zh: '品牌', ja: 'ブランド', es: 'Marca', it: 'Brand', de: 'Marke', nl: 'Merk' }),
    shop: t({ ko: '쇼핑', en: 'Shop', zh: '购物', ja: 'ショップ', es: 'Tienda', it: 'Negozio', de: 'Shop', nl: 'Winkel' }),
    cart: t({ ko: '장바구니', en: 'Cart', zh: '购物车', ja: 'カート', es: 'Carrito', it: 'Carrello', de: 'Warenkorb', nl: 'Winkelwagen' }),
    mypage: t({ ko: '마이페이지', en: 'My Page', zh: '我的', ja: 'マイページ', es: 'Mi página', it: 'Mio profilo', de: 'Mein Konto', nl: 'Mijn pagina' }),
    login: t({ ko: '로그인', en: 'Login', zh: '登录', ja: 'ログイン', es: 'Iniciar sesión', it: 'Accedi', de: 'Anmelden', nl: 'Inloggen' }),
    logout: t({ ko: '로그아웃', en: 'Logout', zh: '退出', ja: 'ログアウト', es: 'Cerrar sesión', it: 'Esci', de: 'Abmelden', nl: 'Uitloggen' }),
    home: t({ ko: '홈', en: 'Home', zh: '首页', ja: 'ホーム', es: 'Inicio', it: 'Home', de: 'Start', nl: 'Home' }),
    scan: t({ ko: '발 측정', en: 'Foot Scan', zh: '足部扫描', ja: 'フットスキャン', es: 'Escaneo de pies', it: 'Scansione piedi', de: 'Fußscan', nl: 'Voetscan' }),
    terms: t({ ko: '이용약관', en: 'Terms', zh: '条款', ja: '利用規約', es: 'Términos', it: 'Termini', de: 'AGB', nl: 'Voorwaarden' }),
    privacy: t({ ko: '개인정보처리방침', en: 'Privacy', zh: '隐私政策', ja: 'プライバシー', es: 'Privacidad', it: 'Privacy', de: 'Datenschutz', nl: 'Privacy' }),
  };

  const mainLinks = [
    { to: '/brand', label: L.brand },
    { to: '/shop', label: L.shop },
  ];
  const isActive = (to) => to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);
  const shopActive = location.pathname.startsWith('/shop') || location.pathname.startsWith('/product');

  const menuItems = [
    ...mainLinks,
    { to: '/cart', label: L.cart },
    ...(isAuthenticated ? [{ to: '/mypage', label: L.mypage }] : [{ to: '/login', label: L.login }]),
    ...(isAuthenticated && user?.role === 'admin' ? [{ to: '/admin', label: 'Admin' }] : []),
    ...(isAuthenticated ? [{ action: handleLogout, label: L.logout }] : []),
  ];
  const menuSub = [
    { to: '/terms', label: L.terms },
    { to: '/privacy', label: L.privacy },
  ];

  const renderMenuItem = (l) => {
    const cls = 'xv-vertical text-[22px] font-medium hover:text-[#ff4d00] transition-colors max-[980px]:text-[18px]';
    if (l.action) return <button key={l.label} onClick={() => { l.action(); setMenuOpen(false); }} className={cls}>{l.label}</button>;
    return <Link key={l.to} to={l.to} className={`${cls} ${isActive(l.to) ? 'text-[#ff4d00]' : ''}`}>{l.label}</Link>;
  };

  const headerText = invertHeader ? 'text-white' : 'text-[#0a0a0a]';

  const tabs = [
    { path: '/', label: L.home, icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
      </svg>
    )},
    { path: '/shop', label: L.shop, icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
      </svg>
    )},
  ];
  const tabsRight = [
    { path: '/cart', label: L.cart, badge: cartCount, icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
      </svg>
    )},
    { path: isAuthenticated ? '/mypage' : '/login', label: isAuthenticated ? 'My' : L.login, icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
      </svg>
    )},
  ];
  const isTabActive = (p) => p === '/' ? location.pathname === '/' : location.pathname.startsWith(p);

  const langButton = <LangPicker lang={lang} setLang={setLang} invert={invertHeader} />;

  return (
    <div className="xv h-[100dvh] md:h-auto md:min-h-screen flex flex-col overflow-hidden md:overflow-visible">
      <SeoHead />

      {/* ── 헤더: 고정 · 투명 ── */}
      <header className={`fixed top-0 left-0 right-0 z-[90] flex items-center justify-between h-[var(--xv-header-h)] px-5 transition-colors ${headerText}`}>
        <Link to="/" className="shrink-0 flex items-center" aria-label="xinvana 홈">
          <img src={logo} alt="xinvana"
            className={`h-[26px] w-auto max-[980px]:h-[20px] ${invertHeader ? 'invert' : ''}`} />
        </Link>

        <nav className="flex items-center gap-9 max-[980px]:hidden">
          {mainLinks.map((l) => (
            <Link key={l.to} to={l.to}
              className={`text-[19px] leading-none transition-colors hover:text-[#c43c00] ${
                (l.to === '/shop' ? shopActive : isActive(l.to)) ? 'xv-underline' : ''
              }`}>
              {l.label}
            </Link>
          ))}
          <Link to="/cart" className="relative text-[19px] leading-none transition-colors hover:text-[#c43c00]">
            {L.cart}
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-3.5 min-w-[18px] h-[18px] px-1 bg-[#ff4d00] text-[#0a0a0a] text-[11px] font-bold flex items-center justify-center">{cartCount}</span>
            )}
          </Link>
          {langButton}
          <button onClick={() => setMenuOpen(true)} aria-label="메뉴 열기" className="ml-1 flex items-center">
            <BarsIcon light={invertHeader} />
          </button>
        </nav>

        <div className="hidden max-[980px]:flex items-center gap-3">
          {langButton}
          <button onClick={() => setMenuOpen(true)} aria-label="메뉴 열기" className="flex items-center">
            <BarsIcon light={invertHeader} />
          </button>
        </div>
      </header>

      {/* ── 사이드 메뉴 ── */}
      {menuOpen && <div className="fixed inset-0 z-[95] bg-black/20" onClick={() => setMenuOpen(false)} />}
      <aside className={`xv-side ${menuOpen ? 'is-open' : ''} fixed top-0 right-0 bottom-0 z-[100] w-[60px] bg-[#0a0a0a] text-white flex flex-col items-center pt-6 pb-5 gap-9 overflow-y-auto max-[980px]:w-[56px] max-[980px]:gap-6`}>
        <button onClick={() => setMenuOpen(false)} aria-label="메뉴 닫기" className="relative w-6 h-10 shrink-0">
          <span className="absolute left-1/2 top-1/2 w-7 h-[2px] bg-white -translate-x-1/2 -translate-y-1/2 rotate-45" />
          <span className="absolute left-1/2 top-1/2 w-7 h-[2px] bg-white -translate-x-1/2 -translate-y-1/2 -rotate-45" />
        </button>
        <nav className="flex flex-col items-center gap-[15px] flex-1">
          {menuItems.map(renderMenuItem)}
          <div className="flex flex-col items-center gap-[15px] pt-3 mt-1 border-t border-black/30">
            {menuSub.map(renderMenuItem)}
          </div>
        </nav>
      </aside>

      {/* ── 본문 ── */}
      <main ref={mainRef} className={`flex-1 overflow-y-auto overflow-x-hidden md:overflow-visible ${fullBleed ? '' : 'pt-[var(--xv-header-h)]'}`}>
        <Outlet context={{ handleMeasureClick }} />

        {/* ── 푸터: 주소 | T/E | 저작권 ── */}
        <footer className="flex items-start justify-between gap-9 px-5 pt-8 pb-9 border-t border-[#d4d4d4] text-[14px] leading-[1.45] max-[980px]:flex-col max-[980px]:gap-2 max-[980px]:text-[12px]">
          <div className="max-[980px]:order-3">
            <img src={logo} alt="xinvana" className="h-[18px] w-auto mb-2.5" />
            <div>{t({ ko: '(주)프린만 · 대표이사 김창호', en: 'Prinmann Corp. · CEO Kim Chang-ho', zh: 'Prinmann Corp. · CEO Kim Chang-ho', ja: 'Prinmann Corp. · CEO Kim Chang-ho', es: 'Prinmann Corp. · CEO Kim Chang-ho', it: 'Prinmann Corp. · CEO Kim Chang-ho', de: 'Prinmann Corp. · CEO Kim Chang-ho', nl: 'Prinmann Corp. · CEO Kim Chang-ho' })}</div>
            <div>{t({ ko: '서울시 영등포구 양평로21가길 19, 선유도 우림라이온스밸리 B동 313호', en: 'B-313, Woorim Lions Valley, 19 Yangpyeong-ro 21ga-gil, Yeongdeungpo-gu, Seoul', zh: '首尔市永登浦区杨坪路21ga街19号 禅游岛禹林狮子谷B栋313号', ja: 'ソウル市永登浦区楊坪路21ga街19 仙遊島ウリムライオンズバレーB棟313号', es: 'B-313, Woorim Lions Valley, 19 Yangpyeong-ro 21ga-gil, Yeongdeungpo-gu, Seúl', it: 'B-313, Woorim Lions Valley, 19 Yangpyeong-ro 21ga-gil, Yeongdeungpo-gu, Seoul', de: 'B-313, Woorim Lions Valley, 19 Yangpyeong-ro 21ga-gil, Yeongdeungpo-gu, Seoul', nl: 'B-313, Woorim Lions Valley, 19 Yangpyeong-ro 21ga-gil, Yeongdeungpo-gu, Seoul' })}</div>
          </div>
          <div className="max-[980px]:order-1">
            <div className="flex gap-[18px]"><span>T</span><a href="tel:18442546" className="xv-hover-point">1844-2546</a></div>
            <div className="flex gap-[18px]"><span>E</span><a href="mailto:product@prinmann.com" className="xv-hover-point">product@prinmann.com</a></div>
          </div>
          <div className="ml-auto text-right max-[980px]:ml-0 max-[980px]:text-left max-[980px]:order-2">
            <div>© {new Date().getFullYear()} xinvana. All rights reserved.</div>
            <div className="flex gap-3 justify-end max-[980px]:justify-start">
              <Link to="/terms" className="xv-hover-point">{L.terms}</Link>
              <Link to="/privacy" className="xv-hover-point">{L.privacy}</Link>
            </div>
          </div>
        </footer>
      </main>

      {/* ── 모바일 하단 탭 ── */}
      <nav className="md:hidden shrink-0 z-50 border-t-[1.5px] border-[#0a0a0a] bg-white" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="flex items-end justify-around h-16 px-1">
          {tabs.map((tab) => (
            <Link key={tab.path} to={tab.path}
              className={`flex flex-col items-center gap-0.5 py-2 px-3 transition-colors ${isTabActive(tab.path) ? 'text-[#ff4d00]' : 'text-[#777]'}`}>
              {tab.icon}
              <span className={`text-[10px] ${isTabActive(tab.path) ? 'font-semibold' : ''}`}>{tab.label}</span>
            </Link>
          ))}

          <button onClick={goMeasure} className="relative -mt-6 shrink-0" aria-label={L.scan}>
            <div className="w-[64px] h-[64px] bg-[#0a0a0a] border-[3px] border-white flex flex-col items-center justify-center gap-0.5 text-white transition-colors hover:bg-[#ff4d00] hover:text-[#0a0a0a]">
              <ScanGlyph className="w-6 h-6" />
              <span className="text-[9px] font-bold tracking-[0.12em]">SCAN</span>
            </div>
          </button>

          {tabsRight.map((tab) => (
            <Link key={tab.path} to={tab.path}
              className={`flex flex-col items-center gap-0.5 py-2 px-3 transition-colors ${isTabActive(tab.path) ? 'text-[#ff4d00]' : 'text-[#777]'}`}>
              <span className="relative">
                {tab.icon}
                {tab.badge > 0 && (
                  <span className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 bg-[#ff4d00] text-[#0a0a0a] text-[9px] font-bold flex items-center justify-center">{tab.badge}</span>
                )}
              </span>
              <span className={`text-[10px] ${isTabActive(tab.path) ? 'font-semibold' : ''}`}>{tab.label}</span>
            </Link>
          ))}
        </div>
      </nav>

      {/* ── PC 플로팅 SCAN ── */}
      <button onClick={goMeasure} title={L.scan}
        className="hidden md:flex fixed z-40 right-6 bottom-8 w-16 h-16 bg-[#0a0a0a] text-white flex-col items-center justify-center gap-0.5 transition-colors hover:bg-[#ff4d00] hover:text-[#0a0a0a]">
        <ScanGlyph className="w-5 h-5" />
        <span className="text-[9px] font-bold tracking-[0.12em]">SCAN</span>
      </button>

      {/* ── PC 측정 안내 모달 ── */}
      {measureModal && (
        <div className="fixed inset-0 bg-black/45 z-[300] flex items-center justify-center p-5" onClick={() => setMeasureModal(false)}>
          <div className="bg-white border-[1.5px] border-[#0a0a0a] w-full max-w-sm p-8 text-center" onClick={(e) => e.stopPropagation()}>
            <ScanGlyph className="w-10 h-10 mx-auto mb-5 text-[#0a0a0a]" />
            <h3 className="text-[22px] font-medium mb-3">
              {t({ ko: '모바일 전용 기능', en: 'Mobile Only', zh: '仅限移动端', ja: 'モバイル専用', es: 'Solo móvil', it: 'Solo mobile', de: 'Nur mobil', nl: 'Alleen mobiel' })}
            </h3>
            <p className="text-[15px] text-[#777] leading-[1.6] mb-7">
              {t({ ko: '발 측정은 스마트폰 카메라를 사용합니다.', en: 'Foot scanning uses your smartphone camera.', zh: '足部扫描需使用智能手机摄像头。', ja: '足の測定はスマートフォンカメラを使用します。', es: 'El escaneo requiere la cámara del smartphone.', it: 'La scansione utilizza la fotocamera dello smartphone.', de: 'Für die Fußvermessung wird deine Smartphone-Kamera benötigt.', nl: 'Voor voetscannen is je smartphonecamera vereist.' })}<br />
              {t({ ko: '스마트폰에서 접속해 주세요.', en: 'Please visit us on your mobile device.', zh: '请通过手机访问使用。', ja: 'スマートフォンでアクセスしてください。', es: 'Por favor, visítanos desde tu móvil.', it: 'Ti preghiamo di visitarci dal tuo smartphone.', de: 'Bitte besuche uns auf deinem Mobilgerät.', nl: 'Bezoek ons op je mobiele apparaat.' })}
            </p>
            <button onClick={() => setMeasureModal(false)} className="xv-btn">
              {t({ ko: '확인', en: 'Got it', zh: '确认', ja: '確認', es: 'Entendido', it: 'Ho capito', de: 'Verstanden', nl: 'Begrepen' })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
